import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Box,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Switch,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { createBacktest } from './backtestsSlice';
import { fetchStrategies } from '../visualStrategies/visualStrategiesSlice';
import { fetchMarketSymbols } from '../markets/marketsSlice';
import LoadingSpinner from '../../components/LoadingSpinner';
import DWLFLoader from '../../components/DWLFLoader';

const CreateBacktestPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isCreating, createError } = useSelector((state) => state.backtests);
  const { strategies, strategiesLoading } = useSelector((state) => state.visualStrategies);
  const { symbols: marketSymbols, status: symbolsStatus, error: symbolsError } = useSelector((state) => state.markets);
  const symbolsLoading = symbolsStatus === 'loading';

  // Form state
  const [formData, setFormData] = useState({
    strategyId: '',
    strategyName: '',
    symbols: [],
    startDate: '',
    endDate: '',
    timeframe: 'daily',
    initialCapital: 10000,
    riskPerTrade: 0.02,
    maxDrawdown: 0.20,
    exitOnlyInProfit: false, // Only trigger exit graph conditions when trade is in profit
    portfolioMode: true, // Shared-capital simulation; false runs each symbol on an independent account (legacy)
    maxConcurrentTrades: 5, // Global cap on positions open at once across all symbols
    allowSymbolPyramiding: false // Allow >1 open position in the SAME symbol (stack on repeat signals); off = one per symbol
  });

  // UI state
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});

  // Use API symbols or fallback to popular symbols
  const availableSymbols = marketSymbols && marketSymbols.length > 0 ? marketSymbols : [
    'AAPL', 'GOOGL', 'MSFT', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX',
    'SPY', 'QQQ', 'IWM', 'GLD', 'SLV', 'TLT', 'VTI', 'VXUS'
  ];

  const steps = [
    {
      label: 'Select Strategy',
      description: 'Choose the visual strategy to backtest'
    },
    {
      label: 'Configure Parameters',
      description: 'Set symbols, dates, and risk parameters'
    },
    {
      label: 'Review & Submit',
      description: 'Review your settings and start the backtest'
    }
  ];

  useEffect(() => {
    // Fetch strategies and symbols when component mounts
    dispatch(fetchStrategies({ limit: 100 }));
    dispatch(fetchMarketSymbols());
  }, [dispatch]);

  // /backtests/new?strategyId=… (e.g. the strategy builder's "Run backtest") pre-selects that strategy
  // once the list has loaded (DWLF-305).
  const [searchParams] = useSearchParams();
  const presetStrategyId = searchParams.get('strategyId');
  useEffect(() => {
    if (!presetStrategyId || formData.strategyId) return;
    const strategy = strategies?.find((st) => st.strategyId === presetStrategyId);
    if (strategy) setFormData((f) => ({ ...f, strategyId: strategy.strategyId, strategyName: strategy.name || '' }));
  }, [presetStrategyId, strategies, formData.strategyId]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleStrategyChange = (strategyId) => {
    const strategy = strategies.find(s => s.strategyId === strategyId);
    setFormData({
      ...formData,
      strategyId,
      strategyName: strategy?.name || ''
    });
    setErrors({ ...errors, strategyId: '' });
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};

    if (stepIndex === 0) {
      if (!formData.strategyId) {
        newErrors.strategyId = 'Please select a strategy';
      }
    }

    if (stepIndex === 1) {
      if (!formData.symbols.length) {
        newErrors.symbols = 'Please select at least one symbol';
      }
      if (!formData.startDate) {
        newErrors.startDate = 'Please select a start date';
      }
      if (!formData.endDate) {
        newErrors.endDate = 'Please select an end date';
      }
      if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
        newErrors.endDate = 'End date must be after start date';
      }
      if (formData.initialCapital <= 0) {
        newErrors.initialCapital = 'Initial capital must be positive';
      }
      if (formData.riskPerTrade <= 0 || formData.riskPerTrade > 1) {
        newErrors.riskPerTrade = 'Risk per trade must be between 0.1% and 100%';
      }
      if (formData.portfolioMode) {
        const mct = formData.maxConcurrentTrades;
        if (!Number.isInteger(mct) || mct < 1 || mct > 20) {
          newErrors.maxConcurrentTrades = 'Max concurrent trades must be an integer between 1 and 20';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) {
      return;
    }

    // maxConcurrentTrades + allowSymbolPyramiding only apply in portfolio mode
    // (pyramiding's per-symbol stack is bounded by the global concurrent cap,
    // which legacy mode doesn't send). Strip both in legacy mode so the engine
    // falls back to its defaults rather than honouring a half-specified config.
    // (maxConcurrentTrades can also sit at '' when cleared while disabled.)
    const { maxConcurrentTrades, allowSymbolPyramiding, ...rest } = formData;
    const payload = formData.portfolioMode
      ? { ...rest, maxConcurrentTrades, allowSymbolPyramiding }
      : rest;

    try {
      await dispatch(createBacktest(payload)).unwrap();
      navigate('/backtests');
    } catch (error) {
      // Error is handled by Redux and displayed in UI
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  if (strategiesLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/backtests')}
            sx={{ mr: 2 }}
          >
            Back to Backtests
          </Button>
          <Typography variant="h4" component="h1">
            Create New Backtest
          </Typography>
        </Box>

        {createError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {createError}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: 3 }}>
          {/* Progress Stepper */}
          <Box>
            <Paper sx={{ p: 2 }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, _index) => (
                  <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="textSecondary">
                        {step.description}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          </Box>

          {/* Form Content */}
          <Box>
            <Paper sx={{ p: 3 }}>
              {/* Step 1: Strategy Selection */}
              {activeStep === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Select Strategy
                  </Typography>
                  <FormControl fullWidth error={!!errors.strategyId}>
                    <InputLabel>Visual Strategy</InputLabel>
                    <Select
                      value={formData.strategyId}
                      onChange={(e) => handleStrategyChange(e.target.value)}
                      label="Visual Strategy"
                    >
                      {strategies.map((strategy) => (
                        <MenuItem key={strategy.strategyId} value={strategy.strategyId}>
                          <Box>
                            <Typography variant="body1">{strategy.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {strategy.description || 'No description'}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.strategyId && (
                      <Typography color="error" variant="caption">
                        {errors.strategyId}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              )}

              {/* Step 2: Configuration */}
              {activeStep === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Configure Parameters
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                      gap: 2
                    }}
                  >
                    <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 4' } }}>
                      <Autocomplete
                        multiple
                        options={availableSymbols}
                        value={formData.symbols}
                        onChange={(event, newValue) => handleInputChange('symbols', newValue)}
                        loading={symbolsLoading}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              {...getTagProps({ index })}
                              key={option}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Symbols"
                            placeholder={symbolsLoading ? 'Loading symbols...' : 'Add symbols...'}
                            error={!!errors.symbols || !!symbolsError}
                            helperText={errors.symbols || (symbolsError && 'Failed to load symbols')}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {symbolsLoading ? <DWLFLoader size="tiny" /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      {symbolsError && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          Using fallback symbols. {symbolsError}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.startDate}
                        helperText={errors.startDate}
                      />
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => handleInputChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.endDate}
                        helperText={errors.endDate}
                      />
                    </Box>
                    <Box>
                      <FormControl fullWidth>
                        <InputLabel>Timeframe</InputLabel>
                        <Select
                          value={formData.timeframe}
                          onChange={(e) => handleInputChange('timeframe', e.target.value)}
                          label="Timeframe"
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        label="Initial Capital"
                        type="number"
                        value={formData.initialCapital}
                        onChange={(e) => handleInputChange('initialCapital', parseFloat(e.target.value))}
                        error={!!errors.initialCapital}
                        helperText={errors.initialCapital}
                        InputProps={{
                          startAdornment: '$'
                        }}
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}>
                      <TextField
                        fullWidth
                        label="Risk per Trade (%)"
                        type="number"
                        value={formData.riskPerTrade * 100}
                        onChange={(e) => {
                          const percentValue = parseFloat(e.target.value) || 0;
                          handleInputChange('riskPerTrade', percentValue / 100);
                        }}
                        error={!!errors.riskPerTrade}
                        helperText={errors.riskPerTrade || 'Percentage of capital to risk per trade (e.g. 2 for 2%)'}
                        inputProps={{ min: 0.1, max: 100, step: 0.1 }}
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}>
                      <TextField
                        fullWidth
                        label="Max Drawdown (%)"
                        type="number"
                        value={formData.maxDrawdown * 100}
                        onChange={(e) => {
                          const percentValue = parseFloat(e.target.value) || 0;
                          handleInputChange('maxDrawdown', percentValue / 100);
                        }}
                        helperText="Maximum acceptable drawdown before stopping (e.g. 20 for 20%)"
                        inputProps={{ min: 1, max: 100, step: 1 }}
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' }, mt: 2 }}>
                      <Tooltip title="When enabled, all symbols share a single capital pool and allocation respects open positions. Disable to backtest each symbol on an independent $10k account (legacy / calibration).">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.portfolioMode}
                              onChange={(e) => handleInputChange('portfolioMode', e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Portfolio mode (shared capital)"
                        />
                      </Tooltip>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Run all symbols through one shared account with capital allocation across concurrent positions. Disable to backtest each symbol on an independent $10k account (legacy).
                      </Typography>
                    </Box>
                    <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' }, mt: 2 }}>
                      <TextField
                        fullWidth
                        label="Max concurrent trades"
                        type="number"
                        value={formData.maxConcurrentTrades}
                        onChange={(e) => {
                          const raw = parseInt(e.target.value, 10);
                          handleInputChange('maxConcurrentTrades', Number.isNaN(raw) ? '' : raw);
                        }}
                        disabled={!formData.portfolioMode}
                        error={!!errors.maxConcurrentTrades}
                        helperText={
                          errors.maxConcurrentTrades
                          || 'Total positions allowed open at the same time across ALL symbols. With pyramiding off, each symbol holds at most one position — so on a single-symbol backtest this has no effect, and across symbols it limits how many different symbols can be in a trade at once. New entries beyond the cap are skipped.'
                        }
                        inputProps={{ min: 1, max: 20, step: 1 }}
                      />
                    </Box>
                    <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' }, mt: 2 }}>
                      <Tooltip title="Off (default): each symbol holds at most one open position at a time — entry signals that fire while a position is already open are skipped. On: the strategy can stack multiple positions in the same symbol (one per entry signal), up to the Max concurrent trades cap.">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.allowSymbolPyramiding}
                              onChange={(e) => handleInputChange('allowSymbolPyramiding', e.target.checked)}
                              disabled={!formData.portfolioMode}
                              color="primary"
                            />
                          }
                          label="Allow symbol pyramiding (stack positions in one symbol)"
                        />
                      </Tooltip>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Off: one open position per symbol — a new signal while you're already in that symbol is skipped (e.g. on a single symbol you get one trade per non-overlapping signal). On: open a fresh position on each signal even while earlier ones are still open, up to Max concurrent trades.
                      </Typography>
                    </Box>
                    <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' }, mt: 2 }}>
                      <Tooltip title="When enabled, exit graph conditions will only trigger when the trade is in profit. Stop loss and take profit still work normally.">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.exitOnlyInProfit}
                              onChange={(e) => handleInputChange('exitOnlyInProfit', e.target.checked)}
                              color="primary"
                            />
                          }
                          label="Exit signals only when in profit"
                        />
                      </Tooltip>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Only trigger exit graph conditions when the trade is profitable
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Step 3: Review */}
              {activeStep === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Review & Submit
                  </Typography>
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Strategy
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {formData.strategyName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Symbols
                          </Typography>
                          <Box>
                            {formData.symbols.map((symbol) => (
                              <Chip
                                key={symbol}
                                label={symbol}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Date Range
                          </Typography>
                          <Typography variant="body1">
                            {formData.startDate} to {formData.endDate}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Timeframe
                          </Typography>
                          <Typography variant="body1">
                            {formData.timeframe}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Initial Capital
                          </Typography>
                          <Typography variant="body1">
                            {formatCurrency(formData.initialCapital)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Risk per Trade
                          </Typography>
                          <Typography variant="body1">
                            {(formData.riskPerTrade * 100).toFixed(1)}%
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Portfolio Mode
                          </Typography>
                          <Typography variant="body1">
                            {formData.portfolioMode ? 'Shared capital' : 'Independent $10k per symbol (legacy)'}
                          </Typography>
                        </Grid>
                        {formData.portfolioMode && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              Max Concurrent Trades
                            </Typography>
                            <Typography variant="body1">
                              {formData.maxConcurrentTrades}
                            </Typography>
                          </Grid>
                        )}
                        {formData.portfolioMode && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                              Symbol Pyramiding
                            </Typography>
                            <Typography variant="body1">
                              {formData.allowSymbolPyramiding ? 'On (stack positions per symbol)' : 'Off (one position per symbol)'}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            Exit Signals Only When in Profit
                          </Typography>
                          <Typography variant="body1">
                            {formData.exitOnlyInProfit ? 'Yes' : 'No'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Navigation Buttons */}
              <Box display="flex" justifyContent="space-between" mt={3}>
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0}
                >
                  Back
                </Button>
                <Box>
                  {activeStep < steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      startIcon={<TrendingUpIcon />}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      startIcon={<PlayArrowIcon />}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <>
                          <DWLFLoader size="tiny" showText={false} />
                          Creating Backtest...
                        </>
                      ) : (
                        'Start Backtest'
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </motion.div>
    </Container>
  );
};

export default CreateBacktestPage; 
